/**
 * Admin → Resources. Rooms / machines / vehicles plus the feature
 * tags that describe what they can do. Both catalogues live here
 * because they're inherently co-edited — a resource is mostly
 * "the things it can do".
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@bookaburra/ui';
import {
  FeaturesCard,
  ResourcesCard,
} from '../components/admin/capabilities-sections';
import { fetchCapabilities } from '../booking-flow/capabilities-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/admin/$slug/resources')({
  component: ResourcesPage,
});

function ResourcesPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const capsQ = useQuery({
    queryKey: ['admin-capabilities', slug],
    queryFn: () => fetchCapabilities(slug),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Resources · ${tenantQ.data.tenant.name} Admin` : 'Resources',
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin-capabilities', slug] });
  }

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 sm:px-12">
      {capsQ.isLoading ? <Skeleton /> : null}
      {capsQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load resources</AlertTitle>
          <AlertDescription>{(capsQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {capsQ.data ? (
        <div className="space-y-8">
          <ResourcesCard
            slug={slug}
            resources={capsQ.data.resources}
            features={capsQ.data.features}
            onChange={invalidate}
          />
          <FeaturesCard
            slug={slug}
            features={capsQ.data.features}
            onChange={invalidate}
          />
        </div>
      ) : null}
    </main>
  );
}

function Skeleton() {
  return (
    <div className="space-y-8">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="h-48 animate-pulse rounded-xl bg-secondary" />
      ))}
    </div>
  );
}
